import { Observable, finalize, debounceTime, distinctUntilChanged, Subscription, takeUntil } from 'rxjs'
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import { DestroyService } from '@app/core/services/destroy.service'

@Component({
  selector: 'lin-improved-select',
  templateUrl: './improved-select.component.html',
  styleUrls: ['./improved-select.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class ImprovedSelectComponent implements OnInit, OnChanges {
  selectControl = new FormControl()
  searchControl = new FormControl()
  searchRequestSubscription: Subscription

  options = []
  filteredOptions = []

  optionsListLoading = false
  loadingMoreOptions = false
  hasMoreOptions = true
  filteredHasMoreOptions = true
  page = 1
  filteredPage = 1

  @Input() required = false
  @Input() disabled = false
  @Input() hasSearch = false
  @Input() staticSearch = false
  @Input() staticList = false
  @Input() controlName: string
  @Input() control: FormControl
  @Input() selectedOption: string
  @Input() preloadedOptions = []
  @Input() optionValueKey: string
  @Input() pinnedOption: unknown
  @Input() pinnedOptionValue: unknown
  @Input() pageSize = 100
  @Input() displayOptionLabel: (option: unknown) => string
  @Input() getOptionsRequest: (params: unknown) => Observable<unknown>
  @Input() requestSearchParamKey: string
  @Input() requestPageParamKey: string

  @Output() selectOption: EventEmitter<unknown> = new EventEmitter<unknown>()

  get searchActive(): string {
    return this.hasSearch && <string>this.searchControl.value
  }

  constructor(private readonly destroyService: DestroyService) {}

  ngOnInit(): void {
    this.initOptions()
    this.selectedOption && this.selectControl.setValue(this.selectedOption)
    this.hasSearch && this.initSearch()
    this.required && this.selectControl.setValidators(Validators.required)
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.preloadedOptions) {
      this.initOptions()
      this.page = 1
    }
  }

  onScroll(event: Event & { target: HTMLElement }): void {
    if (
      this.staticList ||
      !(event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 1) ||
      this.loadingMoreOptions ||
      (!this.searchActive && !this.hasMoreOptions) ||
      (this.searchActive && !this.filteredHasMoreOptions)
    ) {
      return
    }

    this.searchActive ? this.filteredPage++ : this.page++
    this.loadingMoreOptions = true
    this.getOptionsRequest({
      [this.requestPageParamKey]: this.searchActive ? this.filteredPage : this.page,
      ...(this.searchActive && { [this.requestSearchParamKey]: <string>this.searchControl.value }),
    })
      .pipe(
        finalize(() => (this.loadingMoreOptions = false)),
        takeUntil(this.destroyService),
      )
      .subscribe((options: unknown[]) => {
        if (this.searchActive) {
          this.filteredHasMoreOptions = options.length >= 100
          this.filteredOptions.push(...options)
          return
        }

        this.hasMoreOptions = options.length >= 100
        this.options.push(...options)
        this.filteredOptions = this.options
      })
  }

  clearSearch(): void {
    this.searchControl.setValue('')
  }

  handleSelectOption(option): void {
    this.hasSearch && this.clearSearch()
    this.selectOption.emit(option)
  }

  private initOptions(): void {
    this.options = this.preloadedOptions
    this.filteredOptions = this.options
    this.hasMoreOptions = this.preloadedOptions?.length >= this.pageSize
  }

  private initSearch(): void {
    this.staticSearch ? this.initStaticSearch() : this.initDynamicSearch()
  }

  private initDynamicSearch(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroyService))
      .subscribe((value: string) => {
        this.filteredPage = 1
        this.filteredHasMoreOptions = true

        if (this.searchRequestSubscription) {
          this.searchRequestSubscription.unsubscribe()
          this.searchRequestSubscription = null
        }

        if (!value) {
          this.filteredOptions = this.options
          this.optionsListLoading = false
          return
        }

        this.optionsListLoading = true
        this.searchRequestSubscription = this.getOptionsRequest({
          [this.requestSearchParamKey]: value,
          [this.requestPageParamKey]: this.filteredPage,
        })
          .pipe(
            finalize(() => (this.optionsListLoading = false)),
            takeUntil(this.destroyService),
          )
          .subscribe((options: unknown[]) => {
            this.filteredHasMoreOptions = options.length >= 100
            this.filteredOptions = options
          })
      })
  }

  private initStaticSearch(): void {
    this.searchControl.valueChanges.pipe(takeUntil(this.destroyService)).subscribe((value: string) => {
      if (!value) {
        this.filteredOptions = this.options
        this.optionsListLoading = false
        return
      }

      this.filteredOptions = this.options.filter((option) => this.displayOptionLabel(option).toLowerCase().includes(value.toLowerCase()))
    })
  }
}
